// src/app/modules/demandes/TraitementDemandesManager.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AlertCircle, Calendar, Check, Eye, MessageSquare, Search, X } from "lucide-react";

import "../../RessourcesStyles/TraitementDemandes.css";
import DemandeDetailsModal from "./DemandeDetailsModal";

import { fetchAuthorizedCampagnesRequest } from "../../../../../redux/actions/campagnesActions";
import {
    demandeManagerDecisionRequest,
    getDemandesManagerCampagneRequest,
} from "../../../../../redux/actions/demandesActions";

const norm = (value) => String(value ?? "").toLowerCase().trim();

const fmt = (iso) => {
    if (!iso) return "-";
    try {
        const date = new Date(iso);
        if (Number.isNaN(date.getTime())) return String(iso);
        return date.toLocaleString();
    } catch {
        return String(iso);
    }
};

const parseSessionUser = () => {
    try {
        return JSON.parse(sessionStorage.getItem("user") || sessionStorage.getItem("currentUser") || "null");
    } catch {
        return null;
    }
};

const getAuthenticatedUser = (state) =>
    // state?.user?.currentUser ||
    // state?.user?.user ||
    state?.auth?.user ||
    parseSessionUser();

const getCampaignId = (campagne) => campagne?.id ?? campagne?.campaignId ?? campagne?.campagneId;

const isFormationCampaign = (campagne) => {
    const type = String(campagne?.typeCampagne || "").toUpperCase();

    return type === "FORMATION";
};

const isDefaultCampaign = (campagne) =>
    campagne?.isDefault === true ||
    campagne?.defaultCampaign === true ||
    campagne?.parDefaut === true ||
    campagne?.isDefaultFormation === true ||
    campagne?.defaultFormation === true;

const getCampaignLabel = (campagne) =>
    campagne?.nom || campagne?.name || campagne?.libelle || campagne?.title || `Campagne ${getCampaignId(campagne)}`;

const getStatusCode = (demande) =>
    String(demande?.status ?? demande?.managerStatus ?? demande?.managerStepStatus ?? "")
        .trim()
        .toUpperCase();

const getStatusLabel = (demande) => {
    const status = getStatusCode(demande);
    if (["APPROVED", "VALIDATED", "ACCEPTED", "RF_APPROVED", "MANAGER_APPROVED"].includes(status)) return "Validée";
    if (["REJECTED", "REFUSED", "RF_REJECTED", "MANAGER_REJECTED"].includes(status)) return "Rejetée";
    if (["CANCELLED", "WITHDRAWN"].includes(status)) return "Annulée";
    if (["IN_PROGRESS", "ONGOING", "PENDING_RF"].includes(status)) return "En cours";
    if (["PENDING", "PENDING_MANAGER", "PENDING_MANAGER_STEP"].includes(status)) return "En attente";
    return demande?.status || "En attente";
};

const getStatusClass = (demande) => {
    const label = getStatusLabel(demande);
    if (label === "Validée") return "tdStatusBadge--success";
    if (label === "Rejetée") return "tdStatusBadge--danger";
    if (label === "Annulée") return "tdStatusBadge--muted";
    if (label === "En cours") return "tdStatusBadge--info";
    return "tdStatusBadge--warning";
};

const isPendingForManager = (demande) => ["PENDING_MANAGER", "PENDING"].includes(getStatusCode(demande));
const isApproved = (demande) => ["PENDING_RF", "APPROVED"].includes(getStatusCode(demande));
const isRejected = (demande) => getStatusCode(demande) === "REJECTED";

const getDemandeTypeLabel = (demande) => {
    const raw = String(demande?.demandeType || demande?.type || demande?.source || demande?.nature || "").toUpperCase();
    if (["CATALOGUE", "FORMATION"].includes(raw)) return "Catalogue";
    if (["HORS_CATALOGUE", "HORS_CATALOG", "OUT_OF_CATALOG"].includes(raw) || raw.includes("HORS")) return "Hors catalogue";
    if (["EVENT", "EVENT_SESSION", "EVENEMENT"].includes(raw)) return "Evénement";
    if (raw === "SESSION") return "Session";
    return raw || "Autre";
};

export default function TraitementDemandesManager() {
    const dispatch = useDispatch();
    const { loading, managerInbox = [], error } = useSelector((state) => state.demandes || {});
    const { authorizedList: campagnesRaw = [], loading: campagnesLoading } = useSelector((state) => state.campagnes || {});
    const authenticatedUser = useSelector(getAuthenticatedUser);

    const [activeTab, setActiveTab] = useState("enAttente");
    const [search, setSearch] = useState("");
    const [selectedCampaignId, setSelectedCampaignId] = useState("");
    const [campaignInitialized, setCampaignInitialized] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [motifRejet, setMotifRejet] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);
    const managerId = authenticatedUser?.id || sessionStorage.getItem("uid");

    const formationCampaigns = useMemo(
        () => (campagnesRaw || []).filter(isFormationCampaign),
        [campagnesRaw]
    );

    useEffect(() => {
        dispatch(fetchAuthorizedCampagnesRequest());
    }, [dispatch]);

    useEffect(() => {
        if (campaignInitialized || campagnesLoading || !campagnesRaw.length) return;

        const defaultCampaignId = getCampaignId((campagnesRaw || []).find(isDefaultCampaign));
        const defaultFormationCampaign = formationCampaigns.find(
            (campagne) => String(getCampaignId(campagne)) === String(defaultCampaignId)
        );
        const initialCampaign = defaultFormationCampaign || formationCampaigns[0] || null;

        if (initialCampaign) {
            setSelectedCampaignId(String(getCampaignId(initialCampaign)));
        }
        setCampaignInitialized(true);
    }, [campagnesRaw, campaignInitialized, campagnesLoading, formationCampaigns]);

    useEffect(() => {
        if (managerId && selectedCampaignId) {
            dispatch(getDemandesManagerCampagneRequest({
                managerId,
                campaignId: selectedCampaignId,
            }));
        }
    }, [dispatch, managerId, selectedCampaignId]);

    const counts = useMemo(() => {
        const rows = managerInbox || [];
        return {
            enAttente: rows.filter(isPendingForManager).length,
            approuvees: rows.filter(isApproved).length,
            rejetees: rows.filter(isRejected).length,
        };
    }, [managerInbox]);

    const filteredDemandes = useMemo(() => {
        let rows = [...(managerInbox || [])];

        if (activeTab === "enAttente") rows = rows.filter(isPendingForManager);
        if (activeTab === "approuvees") rows = rows.filter(isApproved);
        if (activeTab === "rejetees") rows = rows.filter(isRejected);

        const value = norm(search);
        if (!value) return rows;

        return rows.filter((demande) => {
            const blob = [
                demande.reference,
                demande.numero,
                demande.id,
                demande.formationNom,
                demande.eventTitre,
                demande.targetUserName,
                demande.createdByName,
                getStatusLabel(demande),
                demande.status,
                demande.type,
                demande.campagneNom,
            ].map(norm).join(" ");

            return blob.includes(value);
        });
    }, [managerInbox, activeTab, search]);

    const totalItems = filteredDemandes.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const currentPage = Math.min(page, totalPages);
    const pagedDemandes = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredDemandes.slice(start, start + pageSize);
    }, [filteredDemandes, currentPage, pageSize]);

    useEffect(() => {
        setPage(1);
    }, [search, activeTab, selectedCampaignId, pageSize]);

    const handleCampaignChange = (campaignId) => {
        setSelectedCampaignId(campaignId);
        setCampaignInitialized(true);
        setSearch("");
    };

    const handleViewDetails = (demande) => {
        setSelectedDemande(demande);
        setShowDetailsModal(true);
    };

    const handleApprove = (demande) => {
        if (!demande?.id) return;
        dispatch(demandeManagerDecisionRequest({
            id: demande.id,
            approved: true,
            comment: "",
            managerId,
            campaignId: selectedCampaignId,
        }));
    };

    const handleReject = (demande) => {
        setSelectedDemande(demande);
        setMotifRejet("");
        setShowRejectModal(true);
    };

    const confirmReject = () => {
        if (!selectedDemande?.id || !motifRejet.trim()) return;
        dispatch(demandeManagerDecisionRequest({
            id: selectedDemande.id,
            approved: false,
            comment: motifRejet.trim(),
            managerId,
            campaignId: selectedCampaignId,
        }));
        setShowRejectModal(false);
    };

    return (
        <div className="training-treatment-page tdPage">
            <div className="training-treatment-header">
                <div>
                    <h2>Traitement des demandes</h2>
                </div>
            </div>

            <div className="training-treatment-filters">
                <div className="treatment-filter-campaign">
                    <label>Campagne de formation</label>
                    <select
                        value={selectedCampaignId || ""}
                        onChange={(event) => handleCampaignChange(event.target.value)}
                        disabled={campagnesLoading}
                    >
                        <option value="">
                            {formationCampaigns.length === 0 ? "Aucune campagne de formation disponible" : "Sélectionner une campagne"}
                        </option>
                        {formationCampaigns.map((campagne, index) => (
                            <option key={getCampaignId(campagne) ?? index} value={getCampaignId(campagne) ?? ""}>
                                {getCampaignLabel(campagne)}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="treatment-filter-search">
                    <Search size={16} className="search-icon" />
                    <input
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Rechercher par formation, reference, demandeur ou statut"
                    />
                </div>
            </div>

            {error ? <div className="training-treatment-error">{String(error)}</div> : null}

            <div className="training-treatment-table-card tdCard tdCard--management">
                <div className="treatment-tabs">
                    <button type="button" className={`treatment-tab ${activeTab === "enAttente" ? "is-active" : ""}`} onClick={() => setActiveTab("enAttente")}>
                        <AlertCircle size={16} />
                        En attente
                        <span className={`treatment-tab-count ${activeTab === "enAttente" ? "is-active" : ""}`}>{counts.enAttente}</span>
                    </button>
                    <button type="button" className={`treatment-tab ${activeTab === "approuvees" ? "is-active" : ""}`} onClick={() => setActiveTab("approuvees")}>
                        <Check size={16} />
                        Approuvées
                        <span className={`treatment-tab-count ${activeTab === "approuvees" ? "is-active" : ""}`}>{counts.approuvees}</span>
                    </button>
                    <button type="button" className={`treatment-tab ${activeTab === "rejetees" ? "is-active" : ""}`} onClick={() => setActiveTab("rejetees")}>
                        <X size={16} />
                        Rejetées
                        <span className={`treatment-tab-count ${activeTab === "rejetees" ? "is-active" : ""}`}>{counts.rejetees}</span>
                    </button>
                </div>

                {!selectedCampaignId && formationCampaigns.length === 0 ? (
                    <div className="training-treatment-empty">
                        <AlertCircle size={20} />
                        Aucune campagne de formation autorisée disponible.
                    </div>
                ) : loading ? (
                    <div className="tdEmpty">Chargement...</div>
                ) : filteredDemandes.length === 0 ? (
                    <div className="training-treatment-empty">
                        <AlertCircle size={20} />
                        Aucune demande trouvée.
                    </div>
                ) : (
                    <>
                    <div className="tdTableWrap">
                        <table className="tdTable tdTable--collab">
                            <thead>
                            <tr>
                                <th>Référence</th>
                                <th>Demandeur</th>
                                <th>Formation</th>
                                <th>Type</th>
                                <th>Campagne</th>
                                <th>Date de demande</th>
                                <th>Statut</th>
                                <th className="is-right">Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {pagedDemandes.map((demande) => (
                                <tr key={demande.id}>
                                    <td>
                                        <div className="tdPrimary">{demande.reference || demande.numero || `DF-${demande.id}`}</div>
                                        <div className="tdSub">ID: {demande.id}</div>
                                    </td>
                                    <td>{demande.targetUserName || "-"}</td>
                                    <td className="tdTrunc" title={demande.formationNom || demande.eventTitre || ""}>
                                        {demande.formationNom || demande.eventTitre || "-"}
                                    </td>
                                    <td><span className="tdTypeBadge">{getDemandeTypeLabel(demande)}</span></td>
                                    <td>{demande.campagneNom || "-"}</td>
                                    <td>
                                        <div className="tdDate">
                                            <Calendar size={16} />
                                            {fmt(demande.createdAt)}
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`tdStatusBadge ${getStatusClass(demande)}`}>
                                            {getStatusLabel(demande)}
                                        </span>
                                    </td>
                                    <td className="is-right">
                                        <div className="tdRowActions">
                                            <button type="button" className="tdIconBtn" onClick={() => handleViewDetails(demande)} title="Voir les détails">
                                                <Eye size={16} />
                                            </button>
                                            {activeTab === "enAttente" && (
                                                <>
                                                    <button type="button" className="tdIconBtn tdIconBtn--success" onClick={() => handleApprove(demande)} title="Approuver">
                                                        <Check size={16} />
                                                    </button>
                                                    <button type="button" className="tdIconBtn tdIconBtn--danger" onClick={() => handleReject(demande)} title="Refuser">
                                                        <X size={16} />
                                                    </button>
                                                </>
                                            )}
                                            {activeTab === "approuvees" && (
                                                <button type="button" className="tdIconBtn tdIconBtn--danger" onClick={() => handleReject(demande)} title="Rejeter">
                                                    <X size={16} />
                                                </button>
                                            )}
                                            {activeTab === "rejetees" && (
                                                <button type="button" className="tdIconBtn tdIconBtn--success" onClick={() => handleApprove(demande)} title="Approuver">
                                                    <Check size={16} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                    {totalItems > pageSize ? (
                        <div className="training-treatment-pagination">
                            <div className="training-treatment-pagination-range">
                                {`${(currentPage - 1) * pageSize + 1} - ${Math.min(currentPage * pageSize, totalItems)} sur ${totalItems}`}
                            </div>
                            <div className="training-treatment-pagination-actions">
                                <label className="training-treatment-pagination-size">
                                    <span>Par page</span>
                                    <select value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))}>
                                        <option value={5}>5</option>
                                        <option value={10}>10</option>
                                        <option value={20}>20</option>
                                    </select>
                                </label>
                                <button type="button" className="training-treatment-page-btn" onClick={() => setPage((prev) => Math.max(1, prev - 1))} disabled={currentPage <= 1}>
                                    Précédent
                                </button>
                                <span className="training-treatment-page-indicator">{`Page ${currentPage} / ${totalPages}`}</span>
                                <button type="button" className="training-treatment-page-btn" onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))} disabled={currentPage >= totalPages}>
                                    Suivant
                                </button>
                            </div>
                        </div>
                    ) : null}
                    </>
                )}
            </div>

            <DemandeDetailsModal open={showDetailsModal} demande={selectedDemande} onClose={() => setShowDetailsModal(false)} />

            {showRejectModal && selectedDemande && (
                <div className="tdModal" onMouseDown={(event) => event.target === event.currentTarget && setShowRejectModal(false)}>
                    <div className="tdModal__panel tdModal__panel--small">
                        <div className="tdModal__header">
                            <div className="tdModal__headLeft">
                                <div className="tdModal__icon tdModal__icon--danger">
                                    <MessageSquare size={18} />
                                </div>
                                <div>
                                    <div className="tdModal__title">Refuser la demande</div>
                                    <div className="tdModal__sub">{selectedDemande.targetUserName || "-"} - {selectedDemande.type || "-"}</div>
                                </div>
                            </div>
                            <button type="button" className="tdIconBtn" onClick={() => setShowRejectModal(false)}>
                                <X size={16} />
                            </button>
                        </div>
                        <div className="tdModal__body">
                            <label className="tdLabel">Motif <span className="tdReq">*</span></label>
                            <textarea
                                className="tdTextarea"
                                rows={4}
                                value={motifRejet}
                                onChange={(event) => setMotifRejet(event.target.value)}
                                placeholder="Decrire le motif de rejet..."
                            />
                            <div className="tdHelp">Le motif est obligatoire pour le rejet.</div>
                        </div>
                        <div className="tdModal__footer">
                            <button type="button" className="tdBtn tdBtn--ghost" onClick={() => setShowRejectModal(false)}>Annuler</button>
                            <button type="button" className="tdBtn tdBtn--danger" onClick={confirmReject} disabled={!motifRejet.trim()}>
                                <X size={16} />
                                Confirmer le rejet
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
